/**
 * Which jamb a swing door hangs on.
 *
 * A doorType carries two independent facts: which side of the wall the leaf
 * opens toward (in/out), and which jamb it hangs on (left/right). This module
 * only ever decides the second — it swaps `leftX` <-> `rightX` within one
 * in/out family, so the swing side is preserved by construction.
 *
 * Why the OPEN LEAF and not the swing arc: the swept quarter-arc is the same
 * region for either handing, because it always spans jamb to jamb. Only where
 * the leaf comes to REST distinguishes them — one handing folds the leaf flat
 * against the perpendicular wall, the other plants it across the room.
 */

const { computeOpenLeaf, openLeafFootprint } = require('./doorCollision');
const { getObjectCorners, doPolygonsOverlap } = require('./geometry');

/** The same physical door, hung on the other jamb. */
const OTHER_HAND = {
  leftInswing: 'rightInswing',
  rightInswing: 'leftInswing',
  leftOutswing: 'rightOutswing',
  rightOutswing: 'leftOutswing',
};

function withDoorType(door, doorType) {
  return {
    ...door,
    doorType,
    category: {
      ...(door.category || {}),
      door: { ...(door.category?.door || {}), doorType },
    },
  };
}

/**
 * How much wall is left between a hinge jamb and the nearer end of its wall.
 *
 * A door hinged hard against a perpendicular wall folds flat into the corner
 * and leaves the room clear; hinged at the far jamb it parks the leaf in the
 * middle of the floor. Small stub = good hinge.
 */
function hingeStub(hinge, wall) {
  const a = wall?.alignment?.bottomLeft || wall?.start;
  const b = wall?.alignment?.bottomRight || wall?.end;
  if (!a || !b) return null;
  return Math.min(
    Math.hypot(hinge.x - a.x, hinge.z - a.z),
    Math.hypot(hinge.x - b.x, hinge.z - b.z)
  );
}

/** Fixtures the open leaf would come to rest on top of. */
function leafBlockers(door, walls, obstacles) {
  const leaf = openLeafFootprint(door, walls);
  if (!leaf) return null;
  return (obstacles || []).filter(
    (o) =>
      o?.center && o?.dimensions && doPolygonsOverlap(getObjectCorners(o), leaf)
  );
}

/**
 * Choose the hinge jamb for a swing door, judged in the room its leaf
 * actually sweeps.
 *
 * Preference order:
 *   1. the handing whose open leaf lands on nothing;
 *   2. among equals, the handing hinged nearest a perpendicular wall.
 *
 * Ties and undeterminable geometry keep `doorType` unchanged, so a caller can
 * always use the result verbatim.
 *
 * @param {Object} p
 * @param {Object} p.door - Door with center, rotation, dimensions/width,
 *   parentWallIds, wallFacesPriorityRoom
 * @param {Object[]} p.walls - Walls of the room the leaf sweeps
 * @param {Object[]} [p.obstacles] - Fixtures in that room the leaf must miss;
 *   the caller decides what counts (immovable only, undemolished, etc.)
 * @param {string} [p.doorType] - Overrides the door's own type
 * @returns {{doorType, changed, reason, blockedBy}|null} null for non-swing
 *   types or geometry that will not resolve.
 */
function chooseHingeSide({ door, walls, obstacles = [], doorType } = {}) {
  const current = doorType ?? door?.category?.door?.doorType ?? door?.doorType;
  const other = OTHER_HAND[current];
  if (!other) return null;

  const candidates = [current, other]
    .map((type) => {
      const probe = withDoorType(door, type);
      const leaf = computeOpenLeaf(probe, walls);
      if (!leaf) return null;
      const wall = walls.find((w) => w.id === door.parentWallIds?.[0]);
      return {
        doorType: type,
        blockedBy: leafBlockers(probe, walls, obstacles) || [],
        stub: hingeStub(leaf.hinge, wall),
      };
    })
    .filter(Boolean);

  if (candidates.length < 2) return null;

  const [now, alt] = candidates;
  const nowClear = now.blockedBy.length === 0;
  const altClear = alt.blockedBy.length === 0;

  let winner = now;
  let reason = 'unchanged';
  if (!nowClear && altClear) {
    winner = alt;
    reason = 'open-leaf-blocked';
  } else if (nowClear === altClear && now.stub != null && alt.stub != null) {
    // Both equally (un)blocked — fold the leaf into the tighter corner.
    // Needs a real margin: two jambs the same distance from their nearest
    // wall end is a centred door, where neither handing is better.
    if (alt.stub < now.stub - 0.02) {
      winner = alt;
      reason = 'hinge-nearer-corner';
    }
  }

  return {
    doorType: winner.doorType,
    changed: winner.doorType !== current,
    reason,
    blockedBy: winner.blockedBy,
  };
}

module.exports = { chooseHingeSide, hingeStub, leafBlockers, OTHER_HAND };
